import React, { useEffect, useState } from "react";
import {
    Box,
    Tabs,
    Tab,
    Typography,
} from "@mui/material";
import {
    makeGETRequestOnUserService,
} from "../../Api/api";
import CircularProgress from "@mui/material/CircularProgress";
import { AccountType } from "./AccountType";
import { PanCard } from "./PanCard";
import { AddressProof } from "./AddressProof";
import { ProofOfNRI } from "./ProofOfNRI";
import { BankDetails } from "./BankDetails";
import { DematDetails } from "./DematDetails";
import { NomineeDetails } from "./NomineeDetails";
import { KartaDetails } from "./KartaDetails";
import { RiskProfile } from "./RiskProfile";
import { PersonalDetails } from "./PersonalDetails";
import { OfficeUseActions } from "./OfficeUseActions";
import { CompleteKYCDetails } from "./CompleteKYCDetails";
import { CKYCInformationDetails } from "./CKYCInformationDetails";
import { BusinessProfile } from "./BusinessProfile";
import { OrderHistory } from "./OrderHistory";
import { CashFlowRecords } from "./CashFlowRecords";
import { PortfolioDetails } from "./PortfolioDetails";

const TabPanel = ({ children, value, index }: any) => {
    return value === index ? <Box sx={{ p: 2 }}>{children}</Box> : null;
};

const a11yProps = (index: number) => ({
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
});

const PartnerClientDetails = ({ data, user_type }: { data: any; user_type: string }) => {

    const [activeTab, setActiveTab] = useState(0);
    const [value, setValue] = useState(0);
    const [kycDocs, setKycDocs] = useState<any>({});
    const [loadingKyc, setLoadingKyc] = useState(true);
    const loginUser = JSON.parse(localStorage.getItem("loginUser") || "{}");
    const adminName = loginUser?.name || "";
    const toastr = require("toastr");
    const currentUser = data;
    const [accountTypeState, setAccountTypeState] = useState(currentUser?.accountType || "");
    const isPartner = user_type?.toLowerCase() === "partner";
    const isNRI = accountTypeState === "individual_non_resident";
    const tabOffset = isPartner ? 1 : 0;
    const nriOffset = isNRI ? 1 : 0;

    const fetchKycDocs = async () => {
        if (!currentUser?.uuid) return;

        try {
            setLoadingKyc(true);

            const resp = await makeGETRequestOnUserService(
                `/partneruser/kyc_docs/list/${user_type}/${currentUser.uuid}/`
            );

            if (resp.status === 200) {
                setKycDocs(resp.data);
            }
        } catch (err) {
            console.error(err);
            toastr.error("Failed to load KYC data");
        } finally {
            setLoadingKyc(false);
        }
    };

    useEffect(() => {
        fetchKycDocs();
    }, [currentUser.uuid]);

    const handleTabChange = (_: any, newValue: number) => setActiveTab(newValue);
    const handleChange = (_: any, newValue: number) => setValue(newValue);

    const SectionLoader = () => (
        <Box
            sx={{
                height: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 1
            }}
        >
            <CircularProgress sx={{ color: "#262349" }} />
            <Typography variant="body2" color="text.secondary">
                Loading...
            </Typography>
        </Box>
    );

    const getErrorMessage = (err: any) => {
        if (err?.response?.data) {
            if (typeof err.response.data === "string") return err.response.data;

            if (err.response.data.error) return err.response.data.error;

            if (err.response.data.non_field_errors) {
                return err.response.data.non_field_errors[0];
            }

            return JSON.stringify(err.response.data);
        }

        return "Something went wrong";
    };

    if (!data) return <div>No data available</div>;

    return (
        <Box sx={{ height: "100vh", overflowY: "auto" }}>

            <Box sx={{ backgroundColor: "#262349", borderBottom: "1px solid rgba(255,255,255,0.2)" }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    TabIndicatorProps={{
                        style: {
                            backgroundColor: "#fff",
                            height: 3
                        }
                    }}
                    sx={{
                        minHeight: 48,
                        "& .MuiTab-root": {
                            color: "rgba(255,255,255,0.7)",
                            textTransform: "none",
                        },
                        "& .MuiTab-root.Mui-selected": {
                            color: "#fff !important",
                        }
                    }}
                >
                    <Tab
                        label={`${user_type.charAt(0).toUpperCase() + user_type.slice(1)} KYC`}
                    />
                    <Tab label="Order History" />
                    <Tab label="Nominee" />
                    <Tab label="CashFlow Records" />
                    <Tab label="Risk Profile" />
                    <Tab label="Portfolio" />
                </Tabs>
            </Box>

            <Box sx={{ p: 2 }}>
                {activeTab === 0 && (
                    <Box sx={{ display: "flex" }}>
                        <Tabs
                            orientation="vertical"
                            value={value}
                            onChange={handleChange}
                            sx={{ borderRight: 1, borderColor: "divider", minWidth: 220 }}
                        >
                            {isPartner && (
                                <Tab label="Business Profile" {...a11yProps(0)} />
                            )}
                            <Tab label="Account Type" {...a11yProps(0 + tabOffset)} />
                            <Tab label="Pan Card" {...a11yProps(1 + tabOffset)} />
                            <Tab label="Proof Of Address" {...a11yProps(2 + tabOffset)} />
                            {isNRI && (
                                <Tab label="Proof of NRI" {...a11yProps(3 + tabOffset)} />
                            )}
                            <Tab label="Bank Details" {...a11yProps(3 + tabOffset + nriOffset)} />
                            <Tab label="Demat Details" {...a11yProps(4 + tabOffset + nriOffset)} />
                            <Tab
                                label={
                                    accountTypeState === "Hindu Undivided Family" || accountTypeState === "huf"
                                        ? "Karta Details"
                                        : "Nominee Details"
                                }
                                {...a11yProps(5 + tabOffset + nriOffset)}
                            />
                            <Tab label="Risk Profile" {...a11yProps(6 + tabOffset + nriOffset)} />
                            <Tab label="Personal Details" {...a11yProps(7 + tabOffset + nriOffset)} />
                            <Tab label="Office Use" {...a11yProps(8 + tabOffset + nriOffset)} />
                            <Tab label="Complete KYC" {...a11yProps(9 + tabOffset + nriOffset)} />
                            <Tab label="CKYC Information" {...a11yProps(10 + tabOffset + nriOffset)} />
                        </Tabs>

                        <Box sx={{ flex: 1 }}>
                            {isPartner && (
                                <TabPanel value={value} index={0}>
                                    {loadingKyc ? (
                                        <SectionLoader />
                                    ) : (
                                        <BusinessProfile
                                            currentUser={currentUser}
                                            getErrorMessage={getErrorMessage}
                                        />
                                    )}
                                </TabPanel>
                            )}

                            <TabPanel value={value} index={0 + tabOffset}>
                                {loadingKyc ? (
                                    <SectionLoader />
                                ) : (
                                    <AccountType
                                        accountTypeState={accountTypeState}
                                        setAccountTypeState={setAccountTypeState}
                                        currentUser={currentUser}
                                        user_type={user_type}
                                        setValue={setValue}
                                        getErrorMessage={getErrorMessage}
                                        fetchKycDocs={fetchKycDocs}
                                    />
                                )}
                            </TabPanel>

                            <TabPanel value={value} index={1 + tabOffset}>
                                {loadingKyc ? (
                                    <SectionLoader />
                                ) : (
                                    <PanCard
                                        panData={kycDocs?.pan_card}
                                        adminName={adminName}
                                        kycDocs={kycDocs}
                                        fetchKycDocs={fetchKycDocs}
                                        currentUser={currentUser}
                                        user_type={user_type}
                                        setValue={setValue}
                                        getErrorMessage={getErrorMessage}
                                        accountTypeState={accountTypeState}
                                    />
                                )}
                            </TabPanel>

                            <TabPanel value={value} index={2 + tabOffset}>
                                {loadingKyc ? (
                                    <SectionLoader />
                                ) : (
                                    <AddressProof
                                        poa={kycDocs?.poa}
                                        fetchKycDocs={fetchKycDocs}
                                        currentUser={currentUser}
                                        user_type={user_type}
                                        setValue={setValue}
                                        getErrorMessage={getErrorMessage}
                                        adminName={adminName}
                                        accountTypeState={accountTypeState}
                                    />
                                )}
                            </TabPanel>

                            {isNRI && (
                                <TabPanel value={value} index={3 + tabOffset}>
                                    {loadingKyc ? (
                                        <SectionLoader />
                                    ) : (
                                        <ProofOfNRI
                                            poa={kycDocs?.poa}
                                            currentUser={currentUser}
                                            user_type={user_type}
                                            fetchKycDocs={fetchKycDocs}
                                            getErrorMessage={getErrorMessage}
                                        />
                                    )}
                                </TabPanel>
                            )}

                            <TabPanel value={value} index={3 + tabOffset + nriOffset}>
                                {loadingKyc ? (
                                    <SectionLoader />
                                ) : (
                                    <BankDetails
                                        bank_details={kycDocs?.bank_details || []}
                                        fetchKycDocs={fetchKycDocs}
                                        currentUser={currentUser}
                                        user_type={user_type}
                                        setValue={setValue}
                                        getErrorMessage={getErrorMessage}
                                        isNRI={isNRI}
                                        accountTypeState={accountTypeState}
                                    />
                                )}
                            </TabPanel>

                            <TabPanel value={value} index={4 + tabOffset + nriOffset}>
                                {loadingKyc ? (
                                    <SectionLoader />
                                ) : (
                                    <DematDetails
                                        demat_details={kycDocs?.demat_details || []}
                                        pan_uuid={kycDocs?.pan_card?.uuid || ""}
                                        fetchKycDocs={fetchKycDocs}
                                        currentUser={currentUser}
                                        user_type={user_type}
                                        setValue={setValue}
                                        getErrorMessage={getErrorMessage}
                                    />
                                )}
                            </TabPanel>

                            <TabPanel value={value} index={5 + tabOffset + nriOffset}>
                                {loadingKyc ? (
                                    <SectionLoader />
                                ) : accountTypeState === "Hindu Undivided Family" ||
                                    accountTypeState === "huf" ? (
                                    <KartaDetails
                                        activeTab={value}
                                        currentUser={currentUser}
                                        user_type={user_type}
                                        setValue={setValue}
                                        getErrorMessage={getErrorMessage}
                                    />
                                ) : (
                                    <NomineeDetails
                                        activeTab="kycNominee"
                                        currentUser={currentUser}
                                        user_type={user_type}
                                        setValue={setValue}
                                        getErrorMessage={getErrorMessage}
                                    />
                                )}
                            </TabPanel>

                            <TabPanel value={value} index={6 + tabOffset + nriOffset}>
                                {loadingKyc ? (
                                    <SectionLoader />
                                ) : (
                                    <RiskProfile
                                        activeTab="kycRiskProfile"
                                        currentUser={currentUser}
                                        user_type={user_type}
                                        setValue={setValue}
                                        getErrorMessage={getErrorMessage}
                                    />
                                )}
                            </TabPanel>

                            <TabPanel value={value} index={7 + tabOffset + nriOffset}>
                                {loadingKyc ? (
                                    <SectionLoader />
                                ) : (
                                    <PersonalDetails
                                        personal_details={kycDocs?.personal_details}
                                        fetchKycDocs={fetchKycDocs}
                                        currentUser={currentUser}
                                        user_type={user_type}
                                        setValue={setValue}
                                        getErrorMessage={getErrorMessage}
                                        accountTypeState={accountTypeState}
                                    />
                                )}
                            </TabPanel>

                            <TabPanel value={value} index={8 + tabOffset + nriOffset}>
                                {loadingKyc ? (
                                    <SectionLoader />
                                ) : (
                                    <OfficeUseActions
                                        panData={kycDocs?.pan_card}
                                        poa={kycDocs?.poa}
                                        bank_details={kycDocs?.bank_details || []}
                                        demat_details={kycDocs?.demat_details || []}
                                        currentUser={currentUser}
                                        user_type={user_type}
                                    />
                                )}
                            </TabPanel>

                            <TabPanel value={value} index={9 + tabOffset + nriOffset}>
                                {loadingKyc ? (
                                    <SectionLoader />
                                ) : (
                                    <CompleteKYCDetails
                                        currentUser={currentUser}
                                        user_type={user_type}
                                        adminName={adminName}
                                        bankDetails={kycDocs?.bank_details || []}
                                        dematDetails={kycDocs?.demat_details || []}
                                        panCard={kycDocs?.pan_card}
                                        proofOfAddressDetails={kycDocs?.poa}
                                        activeTab={value}
                                    />
                                )}
                            </TabPanel>

                            <TabPanel value={value} index={10 + tabOffset + nriOffset}>
                                <CKYCInformationDetails
                                    ckycDetails={kycDocs?.ckyc_details}
                                    currentUser={currentUser}
                                />
                            </TabPanel>
                        </Box>
                    </Box>
                )}

                {activeTab === 1 && (
                    <OrderHistory
                        currentUser={currentUser}
                        user_type={user_type}
                    />
                )}

                {activeTab === 2 && (
                    <NomineeDetails
                        activeTab="nomineeDetails"
                        currentUser={currentUser}
                        user_type={user_type}
                        setValue={setValue}
                        getErrorMessage={getErrorMessage}
                    />
                )}

                {activeTab === 3 && (
                    <CashFlowRecords
                        currentUser={currentUser}
                        user_type={user_type}
                    />
                )}

                {activeTab === 4 && (
                    <RiskProfile
                        activeTab="riskProfile"
                        currentUser={currentUser}
                        user_type={user_type}
                        setValue={setValue}
                        getErrorMessage={getErrorMessage}
                    />
                )}

                {activeTab === 5 && (
                    <PortfolioDetails
                        currentUser={currentUser}
                        user_type={user_type}
                    />
                )}
            </Box>
        </Box>
    );
};

export default PartnerClientDetails;